import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';

import { ExecuteRequest, ExecuteRequestOnUrl } from 'core/glambox/GlamboxApi';
import Styles from 'components/person/Instagram.module.scss';
import { PersonActions } from 'state/actions/user/PersonActions';
import { GlamboxEndpoints } from 'core/glambox/GlamboxEndpoints';
import { InstagramInfoSet } from 'state/actions/user/InstagramInfoActions';

// BOOTSTRAPPING
// ----------------------------------------------------------------------

const DUMMY_IMAGE = 'https://s3.amazonaws.com/Glambox.Content.MediaObject/MediaFile_16910.png';
const IG_APPLICATION_ID = process.env.REACT_APP_INSTAGRAM_APP_ID;
const IG_BASE_URL = '//api.instagram.com/oauth/authorize/';
const IG_REDIRECT_URL = process.env.REACT_APP_GB_APP_ROOT + '/register-instagram';
const IG_REQUEST_INFO_TEMPLATE = '//instagram.com/#USER#/?__a=1';

// HELPERS
// ----------------------------------------------------------------------

/**
 * @param personId
 * @returns {function(...[*]=)}
 * @constructor
 */
export const IgConnect = (personId = null) => {
  let url = IG_BASE_URL,
    urlParams = new URLSearchParams();

  urlParams.set('client_id', IG_APPLICATION_ID);
  urlParams.set('response_type', 'code');
  urlParams.set(
    'redirect_uri',
    personId ? `IG_REDIRECT_URL?personId=${personId}` : IG_REDIRECT_URL
  );
  urlParams.set('scope', 'user_profile,user_media');

  window.location = `${url}?${urlParams.toString()}`;
};

/**
 * Solicita dados de perfil do Instagram diretamente do JSON aberto do usuário.
 * @constructor
 */
export const IgProfileRequest = async (username) => {
  let reqUrl = IG_REQUEST_INFO_TEMPLATE.replace(/#USER#/g, username);

  return ExecuteRequestOnUrl(reqUrl, null, 'GET');
};

// BOTÃO CONECTAR/DESCONECTAR
// ----------------------------------------------------------------------

/**
 * Botão de ação do Instagram, condicional.
 *
 * @constructor
 */
export const IgButton = ({ isActive, connectCallback, disconnectCallback }) => {
  if (isActive) {
    return (
      <button className={'btn btn-xs btn-light-pink'} onClick={disconnectCallback}>
        Desconectar
      </button>
    );
  }

  return (
    <button onClick={connectCallback} className={'btn btn-sm btn-instagram'}>
      <i className={'fab fa-instagram fa-2x mr-2'} /> Conecte com o Instagram
    </button>
  );
};

IgButton.defaultProps = {
  isActive: false,
};

IgButton.propTypes = {
  isActive: PropTypes.bool,
  connectCallback: PropTypes.func.isRequired,
  disconnectCallback: PropTypes.func.isRequired,
};

// MINI LISTA DE IMAGENS
// ----------------------------------------------------------------------

/**
 * Componente de exibição para mídia do Instagram.
 *
 * @constructor
 */
export const IgProfileMediaList = ({ id, className, igMedia }) => {
  if (igMedia === null || (Array.isArray(igMedia) && igMedia.length === 0)) {
    return null;
  }

  let _attr = {};
  if (id) _attr.id = id;
  if (className) _attr.className = classnames(className);

  return (
    <div {..._attr}>
      {(() => {
        return igMedia.map((item, key) => {
          return (
            <figure key={key}>
              <img src={item.node.thumbnail_src} alt={''} />
            </figure>
          );
        });
      })()}
    </div>
  );
};

IgProfileMediaList.defaultProps = {
  id: null,
  className: Styles['instagram-user-media'],
  igMedia: null,
};

IgProfileMediaList.propTypes = {
  id: PropTypes.string,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  igMedia: PropTypes.arrayOf(PropTypes.any),
};

// MINI PERFIL
// ----------------------------------------------------------------------

/**
 * Componente de exibição para o perfil do Instagram.
 *
 * @constructor
 */
export const IgProfileCard = ({
  id,
  className,
  instagramInfo,
  instagramData,
  connectCallback,
  disconnectCallback,
}) => {
  let _attr = {};
  if (id) _attr.id = id;
  if (className) _attr.className = classnames(className);

  if (!instagramInfo || !instagramInfo.isActive) {
    return (
      <div {..._attr}>
        <h5>Conta do Instagram</h5>
        <span>
          *Válidos apenas perfis pessoais e abertos ao público" no lugar de "Conta do Instagram"
        </span>
        <IgButton connectCallback={connectCallback} disconnectCallback={disconnectCallback} />
      </div>
    );
  }

  return (
    <div {..._attr}>
      <h5>Conta do Instagram</h5>
      <span>*Válidos apenas perfis pessoais e abertos ao público</span>
      <figure>
        <img src={instagramData ? instagramData.profile_pic_url : DUMMY_IMAGE} alt={''} />
      </figure>
      <section>
        <p>
          <strong>@{instagramInfo.username}</strong>
        </p>
        <p>
          <small>
            <svg width="1em" height="1em" viewBox="0 0 12 12">
              <path
                id="check-green-type-1"
                d="M10.242,10.242a6,6,0,1,0-8.485,0A6,6,0,0,0,10.242,10.242ZM3.849,5.083,5.2,6.432,8.154,3.479,9.2,4.525,6.246,7.478,5.2,8.523,4.152,7.478,2.8,6.129Z"
                fill="#00e381"
              />
            </svg>{' '}
            Conectada
          </small>
        </p>
      </section>
      <IgButton
        connectCallback={connectCallback}
        disconnectCallback={disconnectCallback}
        isActive={instagramInfo.isActive}
      />
    </div>
  );
};

IgProfileCard.defaultProps = {
  id: null,
  className: Styles['instagram-user-card'],
  instagramInfo: null,
  instagramData: null,
};

IgProfileCard.propTypes = {
  id: PropTypes.string,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  instagramInfo: PropTypes.shape({
    dateCreated: PropTypes.number,
    dateUpdated: PropTypes.number,
    isActive: PropTypes.bool,
    personId: PropTypes.number,
    userAccessToken: PropTypes.string,
    userCode: PropTypes.string,
    userId: PropTypes.number,
    userProfilePicture: PropTypes.number,
    username: PropTypes.string,
  }),
  instagramData: PropTypes.any,
  connectCallback: PropTypes.func.isRequired,
  disconnectCallback: PropTypes.func.isRequired,
};

// MINI PERFIL (CONTAINER)
// ----------------------------------------------------------------------

/**
 * Abriga o container de perfil de instagram a ser exibido.
 */
class IgProfile extends Component {
  state = {
    instagramData: null,
    instagramMedia: null,
    loading: false,
  };

  componentDidMount() {
    this.igRequest();
  }

  componentDidUpdate(prevProps) {
    let prevActive = prevProps.instagramInfo ? prevProps.instagramInfo.isActive : false,
      currActive = this.props.instagramInfo ? this.props.instagramInfo.isActive : false;

    if (prevActive !== currActive) {
      this.igRequest();
    }
  }

  igRequest = () => {
    const { instagramInfo } = this.props;

    if ((instagramInfo && instagramInfo.isActive === false) || instagramInfo === null) {
      return null;
    }

    this.setState(
      {
        loading: true,
      },
      async () => {
        let data = await IgProfileRequest(this.props.instagramInfo.username);

        const { result, statusCode } = data;
        const hasOwn = {}.hasOwnProperty;

        if (!result) return null;
        if (statusCode === 200 && hasOwn.call(result, 'graphql')) {
          this.props.igSet(result['graphql']['user'] || null);

          this.setState({
            instagramData: result['graphql']['user'] || null,
            instagramMedia:
              result['graphql']['user']['edge_owner_to_timeline_media']['edges'] || null,
          });
        }
      }
    );
  };

  igConnect = () => {
    IgConnect();
  };

  igDisconnect = () => {
    const { token } = this.props;

    let request = ExecuteRequest(
      GlamboxEndpoints.Person.instagramDisconnect,
      null,
      'GET',
      {
        Authorization: token.Authorization,
      },
      null
    );

    request.then((response) => {
      const { result, statusCode } = response;

      if (statusCode === 200) {
        this.props.igSync();
        this.setState({
          loading: false,
          instagramData: null,
          instagramMedia: null,
        });
      } else {
        this.setState({
          loading: false,
        });
      }
    });
  };

  render() {
    return (
      <>
        <IgProfileCard
          instagramInfo={this.props.instagramInfo}
          instagramData={this.state.instagramData}
          connectCallback={this.igConnect}
          disconnectCallback={this.igDisconnect}
        />

        <IgProfileMediaList igMedia={this.state.instagramMedia} />
      </>
    );
  }
}

IgProfile.defaultProps = {
  instagramInfo: null,
};

IgProfile.propTypes = {
  instagramInfo: PropTypes.shape({
    dateCreated: PropTypes.number,
    dateUpdated: PropTypes.number,
    isActive: PropTypes.bool,
    personId: PropTypes.number,
    userAccessToken: PropTypes.string,
    userCode: PropTypes.string,
    userId: PropTypes.number,
    userProfilePicture: PropTypes.number,
    username: PropTypes.string,
  }),
};

const IgProfileMapState = (state) => ({
  person: state.person.data,
  token: state.token.data,
});

const IgProfileMapDispatch = (dispatch) => ({
  igSync: (insta = null, avatar = null) => {
    dispatch(PersonActions.setInstagram(insta, avatar));
  },
  igSet: (info) => {
    dispatch(InstagramInfoSet(info));
  },
});

export const IgProfileContainer = connect(IgProfileMapState, IgProfileMapDispatch)(IgProfile);
